import React from "react";
import { ColumnDef } from "@tanstack/react-table";
import ActionsMenu from "@/components/dashboard/ActionsMenu";
import { Card, CardType } from "../types";

export interface CardColumnOptions {
  onEdit: (card: Card) => void;
  onDelete: (card: Card) => void;
  cardTypes?: CardType[];
}

export function createCardColumns(options: CardColumnOptions): ColumnDef<Card>[] {
  const typeMap = new Map<number, string>();
  for (const ct of options.cardTypes ?? []) {
    typeMap.set(ct.id, ct.type);
  }

  return [
    { accessorKey: "id", header: "ID" },
    {
      accessorKey: "card_type_id",
      header: "Type",
      cell: ({ row }) => {
        const card = row.original;
        const typeName =
          card.card_type?.type ??
          (card.card_type_id ? typeMap.get(card.card_type_id) : undefined) ??
          (card.card_type_id ? `#${card.card_type_id}` : "—");
        return <span>{typeName}</span>;
      },
    },
    {
      accessorKey: "content",
      header: "Content",
      cell: ({ row }) => {
        const content = row.getValue("content") as string | undefined;
        if (!content) return <span className="text-muted-foreground">—</span>;
        const imgMatch = content.match(/<img[^>]+src="([^"]+)"/);
        const imgSrc = imgMatch?.[1];
        const plain = content.replace(/<[^>]*>/g, "").trim();
        return (
          <div className="flex items-center gap-2 max-w-xs">
            {imgSrc && (
              <img
                src={imgSrc}
                alt="preview"
                className="h-10 w-10 rounded object-cover flex-shrink-0 border"
              />
            )}
            {plain ? (
              <span className="line-clamp-2" title={plain}>{plain}</span>
            ) : !imgSrc ? (
              <span className="text-muted-foreground">—</span>
            ) : null}
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => {
        const raw = row.getValue("created_at") as string | undefined;
        if (!raw) return <span>—</span>;
        const d = new Date(raw);
        const pad = (n: number) => String(n).padStart(2, "0");
        const formatted = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        return <span>{formatted}</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const card = row.original;
        return (
          <ActionsMenu
            onEdit={() => options.onEdit(card)}
            onDelete={() => options.onDelete(card)}
          />
        );
      },
    },
  ];
}
